import AppContext from 'App/AppContext';
import { IPDefaultButton, IPLabel, IPScrollablePane, IPSeparator, IPText } from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPToggle } from 'baseComponents/Toggle/IPToggle';
import GenericModal from 'components/Dialogs/GenericModal';
import AddNewListPanel from 'components/lists/AddNewListPanel';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { TFunction } from 'i18next';
import { DocumentProviderType, getProviderName } from 'models/documentProvider';
import DocumentOutputLocation, { DocumentOutputFolderRule } from 'models/documentOutputLocation';
import OutputLocationBinding, { FolderNameToken } from 'models/outputLocationBinding';
import ResourceList from 'models/resourceList';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiSetTemplateBinding } from 'services/Api/outputLocationBindingService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { CategoryMenuButton, isEligibleCategory } from './CategoryMenu';
import EvidenceFolderSettings from './EvidenceFolderSettings';
import OutputFolderPicker, { canPickOutputFolder } from './OutputFolderPicker';
import { createCategory } from './outputLocationActions';
import { getEvidenceSaveErrorText, getTemplateFolderText } from './outputLocationTexts';

interface ITemplateBindingModalProps {
  isOpen: boolean;
  // The template's row; its own values are undefined where it takes the tenant's.
  binding: OutputLocationBinding;
  // The tenant's location of the purpose: the providers it allows bound the categories the menu offers.
  location: DocumentOutputLocation;
  defaultFolderRule: DocumentOutputFolderRule;
  defaultFolderNameFormat: string;
  tokens: FolderNameToken[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * The evidence settings of one template: its own category or the tenant's, a start folder in that category, and its
 * own folder rule and name format or the tenant's. Saved as one row; a row that takes every value from the tenant is
 * removed by the server. The folder belongs to the category, so a change of category clears it.
 */
const TemplateBindingModal = (props: ITemplateBindingModalProps) => {
  const { t } = useTranslation(['translation', 'outputLocations', 'exceptions']);
  const appContext = useContext(AppContext);
  const [useOwnList, setUseOwnList] = useState<boolean>(props.binding.listId !== undefined);
  const [list, setList] = useState<{ listId: number; name: string } | undefined>(undefined);
  const [folder, setFolder] = useState<{ id: string; name?: string } | undefined>(undefined);
  const [showFolderPicker, setShowFolderPicker] = useState<boolean>(false);
  const [folderRule, setFolderRule] = useState<DocumentOutputFolderRule | undefined>(props.binding.folderRule);
  const [folderNameFormat, setFolderNameFormat] = useState<string | undefined>(props.binding.folderNameFormat);
  const [problems, setProblems] = useState<string[]>([]);
  const [addCategory, setAddCategory] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (!props.isOpen) return;
    setUseOwnList(props.binding.listId !== undefined);
    setList(
      props.binding.listId !== undefined
        ? { listId: props.binding.listId, name: props.binding.listName ?? '' }
        : undefined,
    );
    setFolder(
      props.binding.folderDriveItemId
        ? { id: props.binding.folderDriveItemId, name: props.binding.folderName }
        : undefined,
    );
    setFolderRule(props.binding.folderRule);
    setFolderNameFormat(props.binding.folderNameFormat);
    setProblems([]);
  }, [props.isOpen, props.binding]);

  const listId = useOwnList ? list?.listId : undefined;
  const isChanged =
    listId !== props.binding.listId ||
    folder?.id !== props.binding.folderDriveItemId ||
    folderRule !== props.binding.folderRule ||
    folderNameFormat !== props.binding.folderNameFormat;

  // the category the folder is picked in: the template's own, or the tenant's
  const folderList = appContext.globalDataCache.lists.items.find(
    (item) => item.listId === (useOwnList ? list?.listId : props.location.listId),
  );

  const changeList = (changed: { listId: number; name: string } | undefined) => {
    if (changed?.listId !== list?.listId) setFolder(undefined);
    setList(changed);
  };

  // the tenant's category, which the template takes when it has none of its own
  const getDefaultCategoryText = (): string => {
    if (props.location.listId === undefined) return t('outputLocations:Row.NotSet');

    return `${props.location.listName} (${getProviderName(
      props.location.providerType ?? DocumentProviderType.SharePoint,
    )})`;
  };

  // the new category becomes the template's own when the purpose can use it
  const onCategoryCreated = async (created: ResourceList) => {
    try {
      const saved = await createCategory(appContext, created);
      if (isEligibleCategory(props.location, saved)) {
        changeList({ listId: saved.listId, name: saved.name });
      } else {
        appContext.showNotification(t('exceptions:OutputLocation.NotADocumentLibrary'), true, false, true);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  const save = async () => {
    if (props.binding.templateTaskId === undefined) return;
    try {
      setIsSaving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiSetTemplateBinding(
        props.location.purpose,
        props.binding.templateTaskId,
        listId,
        folder?.id,
        folder?.name,
        folderRule,
        folderNameFormat,
        accessToken,
      );
      appContext.globalDataCache.outputLocationBindings.invalidate();
      props.onSaved();
      props.onClose();
    } catch (err) {
      //shown bottom-center: raised from inside the modal
      const msg =
        getEvidenceSaveErrorText(err, t as TFunction<string[]>) ??
        (err instanceof AppError ? err.message : String(err));
      appContext.showNotification(msg, true, false, true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericModal
      isOpen={props.isOpen}
      title={t('outputLocations:Evidence.TemplateModal.Title', { template: props.binding.templateName ?? '' })}
      subTitle={t('outputLocations:Evidence.TemplateModal.SubTitle')}
      onClose={props.onClose}
      onSave={() => save()}
      keepOpenAfterSave
      saveDisabled={!isChanged || isSaving || problems.length > 0 || (useOwnList && list === undefined)}
      isActionPending={isSaving}
      width={'50vw'}
      maxWidth={640}
      height={'min(90vh, 800px)'}
      titleSeparator
    >
      {/* the modal has a fixed height, so the toggles do not make it jump and only the body scrolls */}
      <IPStack verticalFill>
        <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
            <IPStack tokens={globalStackTokensGapMedium}>
              <IPStack tokens={globalStackTokensGapSmall}>
                <IPLabel>{t('outputLocations:Evidence.Category')}</IPLabel>
                <IPToggle
                  label={t('outputLocations:Evidence.UseDefaultCategory')}
                  inlineLabel
                  checked={!useOwnList}
                  onChange={(_ev, checked) => {
                    setUseOwnList(!checked);
                    setFolder(undefined);
                  }}
                />
                <IPText>
                  {t('outputLocations:Evidence.TemplateModal.Defaults.Category', {
                    category: getDefaultCategoryText(),
                  })}
                </IPText>
                {useOwnList && (
                  <IPStack.Item>
                    <CategoryMenuButton
                      location={props.location}
                      selectedListId={list?.listId}
                      disabled={isSaving}
                      onPick={(picked: ResourceList) => changeList({ listId: picked.listId, name: picked.name })}
                      onCreateNew={() => setAddCategory(true)}
                    />
                  </IPStack.Item>
                )}
              </IPStack>
              <IPStack tokens={globalStackTokensGapSmall}>
                <IPLabel>{t('outputLocations:Evidence.TemplateModal.Folder')}</IPLabel>
                <IPText>
                  {getTemplateFolderText(
                    folder ? folder.name ?? folder.id : undefined,
                    useOwnList,
                    props.location.folderDriveItemId
                      ? props.location.folderName ?? props.location.folderDriveItemId
                      : undefined,
                    t as TFunction<string[]>,
                  )}
                </IPText>
                <IPStack horizontal tokens={globalStackTokensGapSmall}>
                  <IPDefaultButton
                    text={t('outputLocations:Folder.Select')}
                    disabled={isSaving || !canPickOutputFolder(folderList)}
                    onClick={() => setShowFolderPicker(true)}
                  />
                  <IPDefaultButton
                    text={
                      useOwnList
                        ? t('outputLocations:Folder.Root')
                        : t('outputLocations:Evidence.TemplateModal.UseDefaultFolder')
                    }
                    disabled={isSaving || !folder}
                    onClick={() => setFolder(undefined)}
                  />
                </IPStack>
              </IPStack>
              <IPSeparator />
              <EvidenceFolderSettings
                purpose={props.location.purpose}
                isTemplate={true}
                folderRule={folderRule}
                folderNameFormat={folderNameFormat}
                defaultFolderRule={props.defaultFolderRule}
                defaultFolderNameFormat={props.defaultFolderNameFormat}
                tokens={props.tokens}
                disabled={isSaving}
                onChange={(rule, format) => {
                  setFolderRule(rule);
                  setFolderNameFormat(format);
                }}
                onProblemsChange={setProblems}
              />
            </IPStack>
          </IPScrollablePane>
        </IPStack.Item>
      </IPStack>
      {showFolderPicker && folderList && (
        <OutputFolderPicker
          list={folderList}
          onSelect={(folderDriveItemId, folderName) =>
            setFolder(folderDriveItemId ? { id: folderDriveItemId, name: folderName } : undefined)
          }
          onClose={() => setShowFolderPicker(false)}
        />
      )}
      {addCategory && (
        <AddNewListPanel
          isOpen={addCategory}
          renderAsPanel={false}
          onDismiss={() => setAddCategory(false)}
          onAddNew={(created: ResourceList) => onCategoryCreated(created)}
        />
      )}
    </GenericModal>
  );
};

export default TemplateBindingModal;
